import React, { useState, useEffect, useMemo } from 'react';
import { TextField, Paper, Typography, Box, Checkbox, FormControlLabel } from '@mui/material';
import { ContratLocation } from '../../models/models';  // Assuming ContratLocation is defined
import { ButtonType } from '../../constants/enums';
import { createContratLocation, deleteContratLocation, setSelectedContratLocation } from '../../reduxStore/contratLocationSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../reduxStore/Store';
import { useSnackbar } from '../../components/SnackbarProvider';
import MyButton from '../MyButton';
import ClientAutoC from '../AutoComplete/ClientAutoC';
import BienAutoC from '../AutoComplete/BienAutoC';
import { genererNumeroContrat } from '../../utils/utils'; // Assuming this function generates a contract number

interface ContratLocationFormProps {
  onCancel: () => void;
}

const ContratLocationForm: React.FC<ContratLocationFormProps> = ({ onCancel }) => {
  const { selectedContratLocation } = useSelector((state: any) => state.ContratLocationSlice);
  const [resetKey, setResetKey] = useState(0); // Add this line

  const initialiseFormData = useMemo<ContratLocation>(() => ({
    id: -1,
    numero_contrat: genererNumeroContrat(), // Assuming you have a function to generate this
    locataire_id: 0,
    bien_id: 0,
    owner_id: 0,
    user_id: 0,
    date_debut: new Date().toISOString().split('T')[0],
    date_fin: (() => {
      let d = new Date();
      d.setFullYear(d.getFullYear() + 1); return d.toISOString().split('T')[0];
    })(),
    loyer: 0,
    charge: 0,
    frais_enregistrement: 0,
    revision_loyer: true,
    enregistre: false,
    depot_garantie: 0,
    etat: '',
    date_creation: '',
    date_modification: '',
    bien: null, // Assuming you need the bien object
    locataire: null,
    user: null,// Assuming you need the locataire object
    creeFacture: true,

  }), []);

  const [formData, setFormData] = useState<ContratLocation>(initialiseFormData);
  useEffect(() => {
    if (selectedContratLocation) {
      setFormData(selectedContratLocation);
    } else {
      setFormData(initialiseFormData);
    }
  }, [selectedContratLocation, initialiseFormData]);
  

  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    // Check if the input is a checkbox
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value, // Use checked for checkbox, value for other inputs
    }));
  };


  const handleLocataireChange = (locataire: any | null) => {  // Replace `any` with actual type for Locataire
    setFormData((prev) => ({
      ...prev,
      locataire_id: locataire ? locataire.id : null,
      locataire: locataire, // Ensure it updates the object
    }));
  };
  const handleBienChange = (bien: any | null) => {  // Replace `any` with actual type for Locataire
    setFormData((prev) => ({
      ...prev,
      bien_id: bien ? bien.id : null,
      bien: bien, // Ensure it updates the object
      loyer: bien ? bien.loyer : 0, // Assuming you want to set the loyer from the bien
    }));
  };

  const resetForm = () => {
    setFormData(initialiseFormData);
    setResetKey(prev => prev + 1); // Functional update
    dispatch(setSelectedContratLocation(null));
  };

  const handleSubmit = async () => {
    try {
      await dispatch(createContratLocation(formData)).unwrap();
      showSnackbar('Contrat de location créé avec succès!', 'success');
      dispatch(setSelectedContratLocation(null));
      setFormData(initialiseFormData);
      resetForm();
    } catch (error) {
      showSnackbar(error as string || 'Échec de la création du contrat de location', 'error');
    }
  };

  const handleDelete = async () => {
    if (selectedContratLocation && window.confirm('Êtes-vous sûr de vouloir supprimer ce contrat de location ?')) {
      try {
        await dispatch(deleteContratLocation(selectedContratLocation.id ?? 0)).unwrap();
        showSnackbar('Contrat de location supprimé!', 'success');
        dispatch(setSelectedContratLocation(null));
        resetForm();
      } catch (error) {
        showSnackbar(error as string || 'Échec de la suppression', 'error');
      }
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
      <Typography variant="h6" gutterBottom>
        {selectedContratLocation ? 'Modifier un Contrat de Location' : 'Ajouter un Contrat de Location'}
      </Typography>
      <form onSubmit={handleSubmit}>
        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
          <TextField name="numero_contrat" label="N°contrat" value={formData.numero_contrat} onChange={handleChange} fullWidth size="small" />
          <BienAutoC key={`bien${resetKey}`} onBienChange={handleBienChange} selectedBien={formData.bien} />
          <ClientAutoC key={`proprietaire${resetKey}`} onClientChange={handleLocataireChange} selectedClient={formData.locataire} />
          <TextField name="date_debut" label="Date Début" type="date" value={formData.date_debut} onChange={handleChange} fullWidth size="small" InputLabelProps={{ shrink: true }} />
          <TextField name="date_fin" label="Date Fin" type="date" value={formData.date_fin} onChange={handleChange} fullWidth size="small" InputLabelProps={{ shrink: true }} />
          <TextField name="loyer" label="Loyer" type="number" value={formData.loyer ?? ''} onChange={handleChange} fullWidth size="small" />
          <TextField name="etat" label="Statut" value={formData.etat} onChange={handleChange} fullWidth size="small" />
          <TextField name="depot_garantie" label="Dépôt de garantie" type="number" value={formData.depot_garantie ?? ''} onChange={handleChange} fullWidth size="small" />
          <TextField name="charge" label="Charges" type="number" value={formData.charge ?? 0} onChange={handleChange} fullWidth size="small" />
          <TextField name="frais_enregistrement" label="Frais d'enregistrement" type="number" value={formData.frais_enregistrement ?? 0} onChange={handleChange} fullWidth size="small" />
          <div> 
          <FormControlLabel
            control={
              <Checkbox
                name="revision_loyer"
                checked={formData.revision_loyer || false}
                onChange={handleChange}
              />
            }
            label="Révision du loyer"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="enregistre"
                checked={formData.enregistre || false}
                onChange={handleChange}
              />
            }
            label="Contart Enregistré"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="creeFacture"
                checked={formData.creeFacture || false}
                onChange={handleChange}
              />
            }
            label="Cree les factures"
          />
          </div>  
        </Box>
        <Box mt={2} display="flex" justifyContent="space-between">
          <div>
            <MyButton actionType={ButtonType.INSERT} variant="contained" onClick={handleSubmit}>
              {selectedContratLocation ? 'Modifier' : 'Ajouter'}
            </MyButton>
            <MyButton actionType={ButtonType.DELETE} variant="outlined" onClick={handleDelete}>
              Supprimer
            </MyButton>
            <MyButton actionType={ButtonType.CANCEL} variant="outlined" onClick={onCancel}>
              Annuler
            </MyButton>
          </div>
        </Box>
      </form>
    </Paper>
  );
};

export default ContratLocationForm;
